/**
 * Unified API Response Handler for Data Ingestion Platform
 * Handles nested response structures and standardizes success/error detection
 */

// Success statuses returned by the API
const SUCCESS_STATUSES = [
  'success',     // Generic success
  'created',     // Resource created
  'updated',     // Resource updated  
  'deleted',     // Resource deleted
  'triggered',   // Job triggered
  'queued',      // Execution queued
  'completed',   // Execution completed
  'cancellation_requested', // Cancellation requested
  'retrying'     // Execution retry queued
];

// Error statuses returned by the API
const ERROR_STATUSES = [
  'error',       // Generic error
  'failed'       // Operation failed
];

/**
 * Extract response data handling nested structure
 * API returns: {"data": {"status": "created", ...}} or {"status": "created", ...}
 */
export const extractResponseData = (response) => {
  if (!response || !response.data) {
    throw new Error('Invalid API response: missing data');
  }
  
  // Handle nested structure: response.data.data or response.data
  return response.data.data || response.data;
};

/**
 * Check if status indicates success
 */
export const isSuccessStatus = (status) => {
  return SUCCESS_STATUSES.includes(status);
};

/**
 * Check if status indicates error
 */
export const isErrorStatus = (status) => {
  return ERROR_STATUSES.includes(status);
};

/**
 * Extract the machine error code from an axios error.
 * Canonical envelope: {error: {code, message, details}}; tolerates the
 * older flat-string form ({error: "some_code"}).
 */
export const extractErrorCode = (error) => {
  const body = error?.response?.data;
  const err = body?.error ?? body?.data?.error;
  if (err && typeof err === 'object') return err.code;
  if (typeof err === 'string') return err;
  return undefined;
};

/**
 * Extract error message from response.
 * Canonical envelope first ({error: {message}}), then the most specific
 * human message ({message}, {data: {message}}), then the flat legacy
 * forms ({error: "code"}, {status}), then the transport error itself.
 */
export const extractErrorMessage = (error) => {
  if (typeof error === 'string') {
    return error;
  }

  const body = error?.response?.data;
  if (body?.error && typeof body.error === 'object' && body.error.message) {
    return body.error.message;
  }

  if (body?.message) {
    return body.message;
  }

  if (body?.data?.message) {
    return body.data.message;
  }

  if (typeof body?.error === 'string' && body.error) {
    return body.error;
  }

  if (typeof body?.status === 'string' && body.status) {
    return body.status;
  }

  if (error?.message) {
    return error.message;
  }

  return 'An unexpected error occurred';
};

/**
 * Process API response with unified handling
 * @param {Object} response - Axios response object
 * @param {string} operation - Operation name for error context
 * @returns {Object} Processed response data
 */
export const processApiResponse = (response, operation = 'operation') => {
  try {
    const responseData = extractResponseData(response);
    
    // Check for error status
    if (isErrorStatus(responseData.status)) {
      const errorMessage = responseData.message || responseData.error || `${operation} failed`;
      throw new Error(errorMessage);
    }
    
    // Check for success status
    if (isSuccessStatus(responseData.status)) {
      return {
        success: true,
        data: responseData,
        message: responseData.message || `${operation} completed successfully`
      };
    }
    
    // Unknown status - log for debugging but don't fail
    console.warn(`Unknown API status for ${operation}:`, responseData.status);
    return {
      success: true,
      data: responseData,
      message: responseData.message || `${operation} completed`
    };
    
  } catch (error) {
    return {
      success: false,
      error: extractErrorMessage(error),
      data: null
    };
  }
};

/**
 * Handle API errors in saga with standardized error processing
 * @param {Object} error - Error object from axios
 * @param {string} operation - Operation name for context
 * @returns {Object} Standardized error object
 */
export const handleSagaError = (error, operation = 'operation') => {
  const errorMessage = extractErrorMessage(error);
  
  // Log error for debugging
  console.error(`${operation} failed:`, {
    message: errorMessage,
    status: error?.response?.status,
    data: error?.response?.data
  });
  
  return {
    message: errorMessage,
    status: error?.response?.status,
    originalError: error
  };
};

/**
 * Create standardized success action payload
 */
export const createSuccessPayload = (responseData, additionalData = {}) => {
  return {
    ...responseData,
    ...additionalData,
    timestamp: new Date().toISOString()
  };
};

/**
 * Create standardized error action payload
 */
export const createErrorPayload = (error, operation = 'operation') => {
  const errorInfo = handleSagaError(error, operation);
  
  return {
    message: errorInfo.message,
    status: errorInfo.status,
    timestamp: new Date().toISOString(),
    operation
  };
};
